import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import CollaborationDetails from "./CollaborationDetails";
import { useParams, useNavigate } from "react-router-dom";
import { getCollaborationById } from "../../../api/admin/collaborations";
import { updateUserCollaboration } from "../../../api/colaboracoes";
import { listImagesByCollaboration } from "../../../api/admin/images";
import { listCommentsByCollaboration } from "../../../api/admin/comments";
import FlashMessage from "../../../components/common/FlashMessage";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
interface Comments {
  id: number;
  title?: string;
  text?: string;
  created_at?: string;
  autor?: { user_id?: number; nome_completo?: string } | null;
}

const CollaborationRequestAdmin: React.FC = () => {
  const { user, getCsrfToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailStatus, setDetailStatus] = useState<string>();
  const { message, type, showMessage, clearMessage } = useFlashMessage(5000);
  const formatDateForInput = (raw?: string | null) => {
    if (!raw) return "";
    // If already YYYY-MM-DD or contains T
    try {
      // Try Date parsing first
      const maybeISO = raw.includes("T") ? raw.split("T")[0] : raw;
      const d = new Date(maybeISO);
      if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
    } catch {}
    // Try dd/mm/yyyy
    const dm = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dm) {
      const [, dd, mm, yyyy] = dm;
      return `${yyyy}-${mm}-${dd}`;
    }
    // Try yyyy-mm-dd
    const ym = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ym) return raw;
    return "";
  };
  const [formData, setFormData] = useState({
    speciesName: "",
    collectionDate: "",
    country: "",
    region: "",
    state: "",
    city: "",
    latitude: "",
    longitude: "",
    altitude: "",
    collector: "",
    collectorInstitution: "",
    photoAuthor: "",
    photoAuthorInstitution: "",
    sibbio: "",
    sisgen: "",
    observations: "",
    folderName: "",
  });

  const [savedData, setSavedData] = useState(formData);

  const [comments, setComments] = useState<Comments[]>([]);

  const [images, setImages] = useState<
    Array<{ id: number; url: string; status?: string; code?: string }>
  >([]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const { id } = useParams();
  const token = getCsrfToken();

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) { setError("Solicitação não encontrada."); setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        const detail = await getCollaborationById(Number(id), token);
        if (!mounted) return;
        setDetailStatus(detail.status);
        const loaded = {
          speciesName: detail.nome_especie || "",
          collectionDate: formatDateForInput(detail.data || undefined),
          country: detail.pais || "",
          region: detail.regiao || "",
          state: detail.estado || "",
          city: detail.municipio || "",
          latitude: detail.latitude || "",
          longitude: detail.longitude || "",
          altitude: detail.altitude || "",
          collector: detail.coletor || "",
          collectorInstitution: detail.instituicao_coletor || "",
          photoAuthor: detail.autor_foto || "",
          photoAuthorInstitution: detail.instituicao_autor || "",
          sibbio: detail.SISBIO || "",
          sisgen: detail.SISGEN || "",
          observations: detail.observacoes || "",
          folderName: "",
        };
        setFormData(loaded);
        setSavedData(loaded);
        // fetch images
        try {
          const response = await listImagesByCollaboration(Number(id), token!, 1, 100);
          if (mounted)
            setImages(
              response.items.map((i: any) => ({
                id: i.imagem_id,
                url: i.url_imagem,
                status: i.status,
                code: i.codigo_imagem,
              }))
            );
        } catch (err) {
          // ignore image load errors
        }
        // fetch comments for this collaboration
        try {
          const response = await listCommentsByCollaboration(Number(id), token!, 1, 100);
          if (mounted)
            setComments(
              response.items.map((c: any) => ({
                id: c.comentario_id || c.id,
                title: c.assunto || c.titulo || c.title || undefined,
                text:
                  c.conteudo || c.comentario || c.texto || c.text || undefined,
                created_at:
                  c.created_at || c.data_criacao || c.data || undefined,
                autor: c.autor || c.user || null,
              }))
            );
        } catch (err) {
          // ignore comment load errors
        }
      } catch (e) {
        console.error("Erro ao carregar colaboração para edição", e);
        if (mounted) setError("Não foi possível carregar a solicitação.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || loading || error) return;
    if (!id || !token) {
      showMessage("Não foi possível salvar: id ou token ausente.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        nome_especie: formData.speciesName,
        data: formData.collectionDate,
        pais: formData.country,
        regiao: formData.region,
        estado: formData.state,
        municipio: formData.city,
        latitude: formData.latitude,
        longitude: formData.longitude,
        altitude: formData.altitude,
        coletor: formData.collector,
        instituicao_coletor: formData.collectorInstitution,
        autor_foto: formData.photoAuthor,
        instituicao_autor: formData.photoAuthorInstitution,
        SISBIO: formData.sibbio,
        SISGEN: formData.sisgen,
        observacoes: formData.observations,
      };
      await updateUserCollaboration(id, payload, token);
      setSavedData(formData);
      showMessage("Colaboração atualizada com sucesso.", "success");
    } catch (err) {
      console.error(err);
      showMessage("Erro ao salvar alterações. Tente novamente.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollaborationDetails id={id} userName={user?.nome_completo || "Administrador"}
      editing loading={loading} saving={saving} error={error} status={detailStatus}
      values={formData} images={images}
      comments={comments.map((comment) => ({ id: comment.id, title: comment.title, text: comment.text, author: comment.autor?.nome_completo }))}
      feedback={message ? <FlashMessage message={message} type={type} onClose={clearMessage} /> : undefined}
      onBack={() => navigate("/solicitacoes-admin")}
      onChange={handleChange} onSubmit={handleSubmit}
      onCancel={() => { setFormData(savedData); clearMessage(); }} />
  );
};

export default CollaborationRequestAdmin;
